import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  FormHelperText
} from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { Autocomplete } from '@mui/lab';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocationsService from '../../services/LocationsService';
import notify from '../../utils/notifier';
const ALL_LANGUAGE_PATTERN_FOR_ADDRESS = /^[^;:"%$#*={}[\\]|\\`!]*$/; // Adjust to match your backend's version
const BOUNDARY_ID_PATTERN = /^bo[a-z0-9]{14}$/;
import {LOCATION_PRIVILEGES} from '../../utils/constants';
import { hasPrivilege } from "../../utils/privileges_utility";
import { useNavigate } from 'react-router-dom';


let allCities = []

const validateField = (name, value) => {
  switch (name) {
    case 'placeName':
    case 'locality':
    case 'street':
    case 'zipcode':
    case 'city':
    case 'type':
    case 'state':
    case 'countryCode':
    case 'marketingName':
    case 'applicationNamespace':
      if (!ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test(value)) {
        return 'Invalid characters';
      }
      break;

    case 'unlocode':
      if ((value !== '' && value.length != 5) || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test(value)) return 'UNLOCODE code should have 5 characters';
      break;
      
      case 'iataCode':
      if ((value !== '' && value.length != 3) || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test(value)) return 'IATA code should have 3 characters';
      break;
    
    case 'latitude':
      if (value && (value < -90 || value > 90)) return 'Latitude must be between -90 and 90';
      break;

    case 'longitude':
      if (value && (value < -180 || value > 180)) return 'Longitude must be between -180 and 180';
      break;

    case 'boundaryId':
      if (value && !BOUNDARY_ID_PATTERN.test(value)) return 'Invalid boundary ID';
      break;

    case 'metadata':
    case 'addresses':
      try {
        if(value === '') break;
        const parsed = JSON.parse(value);
        if (name === 'addresses' && (!Array.isArray(parsed) || parsed.length > 10)) {
          return 'Must be a valid array with max 10 entries';
        }
      } catch (e) {
        return 'Must be valid JSON';
      }
      break;

    default:
      return '';
  }

  return '';
};

export const DropdownField = ({ label, name, value, onChange, options, error, optional = false }) => (
  <FormControl fullWidth variant="outlined" margin="normal" error={Boolean(error)} required={!optional} >
    <InputLabel>{label}</InputLabel>
    <Select
      name={name}
      value={value}
      onChange={onChange}
      label={label}
    >
      {options.map(opt => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </Select>
    <FormHelperText>{error || ' '}</FormHelperText>
  </FormControl>
);

export const AutoCompleteField = ({ label, name, value, onChange, options, error, optional = false}) => (
  <Autocomplete
    fullWidth
    options={options}
    value={value}
    disableClearable
    onChange={(e, newValue) => onChange({ target: { name, value: newValue || '' } })}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant="outlined"
        margin="normal"
        error={Boolean(error)}
        helperText={error || ' '}
        required={!optional}
      />
    )}
  />
);

export const InputField = ({
  label,
  name,
  value,
  onChange,
  error,
  type = 'text',
  multiline = false,
  optional = false,
}) => (
  <TextField
    fullWidth
    variant="outlined"
    margin="normal"
    type={type}
    name={name}
    label={label}
    value={value}
    onChange={onChange}
    error={Boolean(error)}
    helperText={error || ' '}
    multiline={multiline}
    minRows={multiline ? 2 : undefined}
    required={!optional}
  />
);

function toLabel(str) {
  // Insert space before capital letters, then capitalize the first letter
  const result = str.replace(/([A-Z])/g, ' $1').trim();
  return result.charAt(0).toUpperCase() + result.slice(1);
}

const NODE_TYPES_V2 = [
  'SEA_TERMINAL',
  'ICD',
  'AIR_TERMINAL',
  'ZONE',
  'CFS',
  'RAIL_TERMINAL',
  'TPT',
  'PFT',
  'PORT',
  'YARD',
  'FERRY_PORT',
  'ROAD_TERMINAL'
]

const additionalFields = {
  applicationNamespace: true,
  countryCode: true,
  state: true,
  city: true,
  type: true,
  iataCode: true,
  unlocode: true,
  placeName: true,
  locality: false,
  street: false,
  zipcode: true,
  boundaryId: false,
  latitude: false,
  longitude: false,
  addresses: false,
  metadata: false,
  alternateNames: false,
  marketingName: false,
  tags: false,
  isDpwOwned: false,
  isActive: false
}

const requiredFields = {
  applicationNamespace: true,
  countryCode: true,
  state: true,
  city: true,
  type: true,
  iataCode: true,
  unlocode: true,
  placeName: true,
  locality: false,
  street: false,
  zipcode: true,
  boundaryId: false,
  latitude: false,
  longitude: false,
  addresses: false,
  metadata: false,
  alternateNames: false,
  marketingName: false,
  tags: false,
  isDpwOwned: false,
  isActive: false
}

const UploadLocationForm = () => {
  const defaultApplicationNamespace = localStorage.getItem('_defaultApplicationNamespace');
  const applicationNamespaces = JSON.parse(localStorage.getItem('_applicationNamespaces'));
  const applicationNamespaceLocationTypes = JSON.parse(localStorage.getItem('_locationTypes'));
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const defaultValues = {
    applicationNamespace: defaultApplicationNamespace || '',
    countryCode: 'IN',
    state: '',
    city: '',
    type: 'PLACE',
    iataCode: '',
    unlocode: '',
    placeName: '',
    locality: '',
    street: '',
    zipcode: '',
    boundaryId: '',
    latitude: '',
    longitude: '',
    addresses: '',
    metadata: '',
    alternateNames: '',
    marketingName: '',
    tags: '',
    isDpwOwned: false,
    isActive: true,
  };

  const [formData, setFormData] = useState(defaultValues);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locationTypes, setLocationTypes] = useState(applicationNamespaceLocationTypes[defaultApplicationNamespace] || []);
  const requiredFieldKeys = Object.keys(additionalFields).filter(key => additionalFields[key]);
  const optionalFieldKeys = Object.keys(additionalFields).filter(key => !additionalFields[key]);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasPrivilege(LOCATION_PRIVILEGES['UPLOAD'])){
      navigate('/unauthorized');
    }
    if (!countries.length) {
      getCountriesData();
    }
  }, [countries]);

  useEffect(() => {
    if (formData.countryCode) {
      getCitiesData(formData.countryCode);
    }
  }, [formData.countryCode]);

  useEffect(() => {
    if (formData.state) {
      updateCities(formData.state);
    }
  }, [formData.state]);

  const getCountriesData = async () => {
    try {
      const response = await LocationsService.getCountriesData();
      const countriesData = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setCountries(countriesData);
    } catch (error) {
      notify({ type: 'error', message: 'Error fetching countries data' });
    }
  };

  const updateCities = async (stateName) => {
    try {
      const curCities = allCities.filter(city => city.stateName === stateName).map(city => city.name);
      setCities(curCities);
    } catch (error) {
      notify({ type: 'error', message: 'Error updating cities data' });
    }
  };

  const getCitiesData = async (countryCode) => {
    try {
      const query = { _countryIso2: countryCode };
      const response = await LocationsService.getCitiesData(query);
      
      allCities = response.data
      const tempStates = [...new Set(allCities.map(city => city.stateName))];
      
      setStates(tempStates);
      setCities(allCities.map(city => city.name));
    } catch (error) {
      notify({ type: 'error', message: 'Error fetching cities data' });
    }
  };

  const handleSubmit = async (e) => {
    setLoading(true); 
    e.preventDefault();
    let processedData = {};
    let newErrors = {};
    
    if(formData['placeName'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('placeName')){
      newErrors['placeName'] = 'Invalid value'
    }else{
      processedData['placeName'] = formData['placeName'];
    }

    if(!ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('locality')){
      newErrors['locality'] = 'Invalid value'
    }else if(formData['locality'] != ''){
      processedData['locality'] = formData['locality'];
    }

    if(!ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('street')){
      newErrors['street'] = 'Invalid value'
    }else if(formData['street'] != ''){
      processedData['street'] = formData['street'];
    }

    if(!ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('zipcode')){
      newErrors['zipcode'] = 'Invalid value'
    }else if(formData['zipcode'] != ''){
      processedData['zipcode'] = formData['zipcode'];
    }

    if(formData['type'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('type')){
      newErrors['type'] = 'Invalid value'
    }else{
      processedData['type'] = formData['type'];
    }

    if(formData['city'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('city')){
      newErrors['city'] = 'Invalid value'
    }else{
      processedData['city'] = formData['city'];
    }

    if(formData['state'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('state')){
      newErrors['state'] = 'Invalid value'
    }else{
      processedData['state'] = formData['state'];
    }

    if(formData['countryCode'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('countryCode')){
      newErrors['countryCode'] = 'Invalid value'
    }else{
      processedData['countryCode'] = formData['countryCode'];
    }

    if(!ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('marketingName')){
      newErrors['marketingName'] = 'Invalid value'
    }else if(formData['marketingName'] != ''){
      processedData['marketingName'] = formData['marketingName'];
    }

    if(formData['applicationNamespace'] == '' || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test('applicationNamespace')){
      newErrors['applicationNamespace'] = 'Invalid value'
    }else{
      processedData['applicationNamespace'] = formData['applicationNamespace'];
    }

    if(formData['unlocode'].length === 5 && ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test(formData['unlocode'])){
      processedData['unlocode'] = formData['unlocode'];
    }else if(formData['unlocode'].length === 0 && NODE_TYPES_V2.includes(formData['type'])){
      newErrors['unlocode'] = 'UNLOCODE is mandatory'
    }else if(formData['unlocode']){
      newErrors['unlocode'] = 'Please enter valid unlocode.'
    }

    if(formData['type'] === 'AIR_TERMINAL' && ((formData['iataCode'].length != 3) || !ALL_LANGUAGE_PATTERN_FOR_ADDRESS.test(formData['iataCode']))){
      newErrors['iataCode'] = 'IATA code should have 3 characters'
    }else if(formData['type'] == 'AIR_TERMINAL'){
      processedData['iataCode'] = formData['iataCode'];
    }

    if(formData['latitude'] != '' && formData['longitude'] != ''){
      if(formData['latitude'] < -90 || formData['latitude'] > 90){
        newErrors['latitude'] = 'Latitude must be between -90 and 90'
      } else {
        processedData['latitude'] = parseInt(formData['latitude']);
      }

      if(formData['longitude'] < -180 || formData['longitude'] > 180){
        newErrors['longitude'] = 'Longitude must be between -180 and 180'
      } else {
        processedData['longitude'] = parseInt(formData['longitude']);
      }
    }

    if(formData['latitude'] != '' || formData['longitude'] != ''){
      if(formData['latitude'] == ''){
        newErrors['latitude'] = 'If longitude is given latitude must be there'
      }
      
      if(formData['longitude'] == ''){
        newErrors['longitude'] = 'If latitude is given longitude must be there'
      }
    }
    
    if(formData['boundaryId'] != '' && !BOUNDARY_ID_PATTERN.test(formData['boundaryId'])){
      newErrors['boundaryId'] = 'Invalid boundary ID'
    }else if(formData['boundaryId'] != ''){
      processedData['boundaryId'] = formData['boundaryId'];
    }

    if(formData['metadata'] != ''){
      try {
        const parsed = JSON.parse(formData['metadata']);
        if ((!Array.isArray(parsed) || parsed.length > 10)) {
          newErrors['metadata'] = 'Must be a valid array with max 10 entries';
        }else{
          processedData['metadata'] = parsed;
        }
      } catch (e) {
        newErrors['metadata'] = 'Must be valid JSON';
      }
    }

    if(formData['addresses'] != ''){
      try {
        const parsed = JSON.parse(formData['addresses']);
        processedData['addresses'] = parsed;
      } catch (e) {
        newErrors['addresses'] = 'Must be valid JSON';
      }
    }

    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setLoading(false); 
      notify({ type: 'error', message: 'Please fix validation errors' });
      return;
    }
    try {
      const response = await LocationsService.createLocation(processedData);
      if (response?.status === 201) {
        notify({ type: 'success', message: `Location uploaded successfully : ${response.data.locationId}`});
        setFormData(defaultValues);
      } else {
        notify({ type: 'error', message: 'Unexpected response from server' });
      }
    } catch (err) {
      // Show the error message coming from backend
      notify({ type: 'error', message: `${err.message}` });
    } finally {
      setLoading(false); 
    } 
  };
  


const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData((prev) => ({ ...prev, [name]: value }));

  const errorMsg = validateField(name, value);
  setFormErrors((prev) => ({ ...prev, [name]: errorMsg }));

  if (name === 'applicationNamespace') {
    setLocationTypes(applicationNamespaceLocationTypes[value] || []);
    setFormData((prev) => ({ ...prev, type: 'PLACE', iataCode: '', unlocode: ''}));
  }
  if(name === 'countryCode'){
    setFormData((prev) => ({ ...prev, state: '', city: ''}));
  }
  if(name === 'state'){
    setFormData((prev) => ({ ...prev, city: ''}));
  }
  if(name === 'city' && formData['state'] === ''){
    let cityObj = allCities.find(city => {
      return value === city['name'];
    });
    let stateName = '';
    if(cityObj){
      stateName = cityObj['stateName'];
    }
    setFormData((prev) => ({ ...prev, state: stateName}));
  }
  if(name === 'type'){
    setFormData((prev) => ({ ...prev, iataCode: '', unlocode: ''}));
  }
};

  const renderField = (field, isOptional = false) => {
    if(formData.type !== 'AIR_TERMINAL' && field === 'iataCode') return;
    if( formData.type === 'AIR_TERMINAL' && field === 'unlocode') return;

    const commonProps = {
      name: field,
      value: formData[field],
      onChange: handleChange,
      error: formErrors[field],
      optional: isOptional
    };

    switch (field) {
      case 'isDpwOwned':
        return <DropdownField label="is DPW Owned" options={["true", "false"]} {...commonProps} />;
      case 'isActive':
        return <DropdownField label="is Active" options={["true", "false"]} {...commonProps} />;
      case 'applicationNamespace':
        return <DropdownField label="Application Namespace" options={applicationNamespaces} {...commonProps} />;
      case 'type':
        return <DropdownField label="Location Type" options={locationTypes} {...commonProps} />;
      case 'countryCode':
        return <AutoCompleteField label="Country Code" options={countries.map(c => c.iso2)} {...commonProps} />;
      case 'state':
        return <AutoCompleteField label="State" options={states} {...commonProps} />;
      case 'city':
        return <AutoCompleteField label="City" options={cities} {...commonProps} />;
      case 'iataCode':
        return <InputField label="IATA Code" {...commonProps} />;
      case 'unlocode':
        return <InputField label="Unlocode" {...commonProps} />;
      case 'metadata':
        return <InputField label="Metadata" {...commonProps} />;
      case 'addresses':
        return <InputField label="Address" {...commonProps} />;
      default:
        return <InputField label={toLabel(field)} type={['latitude', 'longitude'].includes(field) ? 'number' : 'text'} {...commonProps} />;
    }
  };

  return (
    <div style={{ backgroundColor: '#F2F2F2', padding: '20px 40px' }}>
      <Typography variant="h5" gutterBottom><b>Upload Location</b></Typography>
      <Paper style={{ padding: 20, backgroundColor: '#fff' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {requiredFieldKeys.map(field => {
              let optional = false;
              
              if(field === 'unlocode' && formData.type === 'PLACE') optional = true;
              if(field === 'zipcode' && formData.type !== 'PLACE') optional = true;

              let renderedField = renderField(field, optional)
              if(renderedField) return <Grid item xs={12} sm={6} md={3} key={field}>{renderedField}</Grid>
            })}

            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={() => setShowOptionalFields(!showOptionalFields)}
                endIcon={showOptionalFields ? <ExpandLess /> : <ExpandMore />}
                style={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 8,
                  transition: 'background-color 0.3s ease'
                }}
              >
                {showOptionalFields ? 'Hide Additional Fields' : 'Show Additional Fields'}
              </Button>
            </Grid>

            {showOptionalFields && optionalFieldKeys.map(field => (
              <Grid item xs={12} sm={6} md={3} key={field}>{renderField(field, true)}</Grid>
            ))}

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                style={{ float: 'right', paddingInline: '30px' }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default UploadLocationForm;
